import { addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { createNumberedDoc } from '@/lib/firestore/numbering';
import { companyActivityCol, companyDoc } from '@/lib/firestore/refs';
import { addCompanyToScope, removeCompanyFromScope } from '@/features/agents/api';
import { logAudit } from '@/lib/records/audit';
import { companyCode } from '@shared/crm';
import type { AccountTeamMember, Company, CompanyActivity, PrincipalRef, SpecialHandlingNote } from '@shared/types';

export type CompanyInput = Pick<Company, 'name' | 'type' | 'tier' | 'industry' | 'website' | 'domains' | 'phone' | 'address' | 'timezone' | 'parentCompanyId' | 'accountManagerUid' | 'slaPolicyId'>;

const note = (id: string, body: string, by: PrincipalRef) => addDoc(companyActivityCol(id), { type: 'system', body, by, at: serverTimestamp() } as never);

export async function createCompany(input: CompanyInput, by: PrincipalRef): Promise<string> {
  const id = await createNumberedDoc(db, 'ACC', 'companies', () => ({
    ...input, code: companyCode(input.name), status: 'onboarding', accountTeam: [], primaryContactId: null, specialHandlingNotes: [], tags: [], openCaseCount: 0,
    createdAt: serverTimestamp(), createdBy: by, updatedAt: serverTimestamp(), updatedBy: by,
  }));
  await note(id, `Company created by ${by.name}`, by);
  await logAudit(by, 'company.create', { type: 'company', id }, { after: { name: input.name } });
  return id;
}
export async function updateCompany(id: string, patch: Partial<CompanyInput & Pick<Company, 'primaryContactId' | 'tags'>>, by: PrincipalRef): Promise<void> {
  await updateDoc(companyDoc(id), { ...patch, updatedAt: serverTimestamp(), updatedBy: by });
  await logAudit(by, 'company.update', { type: 'company', id }, { after: patch });
}
export async function setCompanyStatus(id: string, status: Company['status'], by: PrincipalRef): Promise<void> {
  await updateDoc(companyDoc(id), { status, updatedAt: serverTimestamp(), updatedBy: by });
  await note(id, `Status set to ${status}`, by);
  await logAudit(by, `company.status.${status}`, { type: 'company', id });
}
export async function addTeamMember(company: Pick<Company, 'id' | 'accountTeam'>, member: AccountTeamMember, by: PrincipalRef): Promise<void> {
  if (company.accountTeam.some((m) => m.principal.id === member.principal.id)) throw new Error(`${member.principal.name} is already on the account team.`);
  const accountTeam = [...company.accountTeam, member];
  await updateDoc(companyDoc(company.id), { accountTeam, updatedAt: serverTimestamp(), updatedBy: by });
  await note(company.id, `${member.principal.name} added as ${member.role.replace('_', ' ')}`, by);
  if (member.principal.type === 'agent') await addCompanyToScope(member.principal.id, company.id);
  await logAudit(by, 'company.team.add', { type: 'company', id: company.id }, { after: member });
}
export async function removeTeamMember(company: Pick<Company, 'id' | 'accountTeam'>, principalId: string, by: PrincipalRef): Promise<void> {
  const removed = company.accountTeam.find((m) => m.principal.id === principalId);
  const accountTeam = company.accountTeam.filter((m) => m.principal.id !== principalId);
  await updateDoc(companyDoc(company.id), { accountTeam, updatedAt: serverTimestamp(), updatedBy: by });
  if (removed?.principal.type === 'agent') await removeCompanyFromScope(principalId, company.id);
  await logAudit(by, 'company.team.remove', { type: 'company', id: company.id }, { before: { principalId } });
}
export async function saveSpecialHandling(id: string, notes: SpecialHandlingNote[], by: PrincipalRef): Promise<void> {
  await updateDoc(companyDoc(id), { specialHandlingNotes: notes, updatedAt: serverTimestamp(), updatedBy: by });
  await logAudit(by, 'company.special_handling', { type: 'company', id }, { after: { count: notes.length } });
}
export async function addCompanyNote(id: string, type: CompanyActivity['type'], body: string, by: PrincipalRef): Promise<void> {
  await addDoc(companyActivityCol(id), { type, body, by, at: serverTimestamp() } as never);
}
